import api from './client'

export interface StudentProgressHistoryItem {
  id: number
  title: string
  create_datetime: string | null
  rw_score: number
  math_score: number
  total_score: number
  rw_hard_flag: boolean
  math_hard_flag: boolean
}

export interface StudentPracticeRecordItem {
  id: number
  title: string
  create_datetime: string | null
  rw_score: number
  math_score: number
}

export interface StudentEvaluationHistoryResponse {
  progress: StudentProgressHistoryItem[]
  practice_records: StudentPracticeRecordItem[]
}

export const evaluationsApi = {
  history: () => api.get<StudentEvaluationHistoryResponse>('/evaluations/history'),
}

export interface EvaluationTemplate {
  id: number
  title: string
  eval_type: string
  is_active: boolean
}

export interface EvaluationListItem {
  id: number
  title: string
  tbl_user_id: number
  user_name: string | null
  rw_score: number | null
  math_score: number | null
  create_datetime: string | null
}

export interface EvalQuestionChoice {
  id: number
  choice_label: string
  choice_text: string
  display_order: number
}

export interface EvalQuestionAnswer {
  id: number
  question: string
  question_image: string | null
  question_type: string
  correct_answer_text: string | null
  choices: EvalQuestionChoice[]
}

export interface EvalQuestionPayload {
  detail_id: number
  sequence: number
  module: string | null
  question_set_id: number
  question_text: string | null
  question_images: string[]
  question_answers: EvalQuestionAnswer[]
}

export interface EvalPrepareResponse {
  evaluation_id: number
  total_questions: number
  questions: EvalQuestionPayload[]
  modules: { module: string; count: number }[]
  saved_answers?: Record<number, string>
  assignment_type?: string
}

export interface EvalMasteryResult {
  mastery_id: number
  mastery_name: string
  correct_count: number
  wrong_count: number
}

export interface EvalSummaryResponse {
  evaluation_id: number
  rw_score: number
  math_score: number
  total_score: number
  mastery_results: EvalMasteryResult[]
  assignment_type?: string
}

export function getEvaluationTemplates() {
  return api.get<EvaluationTemplate[]>('/evaluations/templates')
}

export function getEvaluations() {
  return api.get<EvaluationListItem[]>('/evaluations')
}

export function createEvaluation(data: {
  title: string
  evaluation_template_id: number
  student_ids?: number[]
  roster_id?: number
  plan_id?: number
  insert_position?: number
  insert_append?: boolean
}) {
  return api.post('/evaluations', data)
}

export function prepareEvaluation(uuid: string) {
  return api.post<EvalPrepareResponse>(`/evaluations/prepare/${uuid}`)
}

export function submitEvalAnswer(detail_id: number, user_answer: string) {
  return api.post('/evaluations/answer', { detail_id, user_answer })
}

export function submitEvalAnswersBulk(answers: { detail_id: number; user_answer: string }[]) {
  return api.post('/evaluations/answers/bulk', { answers })
}

export function submitEvaluation(evaluation_id: number) {
  return api.post<EvalSummaryResponse>(`/evaluations/submit/${evaluation_id}`)
}

export interface CompleteModuleResponse {
  questions: EvalQuestionPayload[]
  hard_flag: boolean
  wrong_count: number
  completed_module: string
}

export function completeModule(evaluation_id: number, module: string) {
  return api.post<CompleteModuleResponse>('/evaluations/complete-module', {
    evaluation_id,
    module,
  })
}

export interface EvalResultResponse {
  evaluation_id: number
  title: string
  rw_score: number
  math_score: number
  total_score: number
  total_questions: number
  mastery_results: EvalMasteryResult[]
  create_datetime: string | null
  assignment_type?: string
  grading_score: number | null
  graded_at: string | null
  tutor_feedback: string | null
}

export function getEvaluationResult(evaluation_id: number) {
  return api.get<EvalResultResponse>(`/evaluations/result/${evaluation_id}`)
}

export interface ReviewChoiceData {
  id: number
  choice_label: string
  choice_text: string
  is_correct: boolean
  explanation: string | null
  display_order: number
}

export interface ReviewAnswerData {
  id: number
  question: string | null
  question_image: string | null
  question_type: string
  correct_answer_text: string | null
  explanation: string | null
  choices: ReviewChoiceData[]
}

export interface EvalQuestionReview {
  page: number
  total: number
  detail_id: number
  sequence: number
  module: string | null
  user_answer: string | null
  is_correct: boolean
  question_set_id: number
  question_text: string | null
  question_images: string[]
  question_answers: ReviewAnswerData[]
}

export function getEvaluationQuestionReview(evaluation_id: number, page: number) {
  return api.get<EvalQuestionReview>(`/evaluations/result/${evaluation_id}/questions`, { params: { page } })
}
